import { api } from '../api';

export interface CleanupTaskDescriptor {
  key: string;
  previewEndpoint: string;
  applyEndpoint: string;
  description: string;
}

export interface RolesPermissionsCleanupPayload {
  userIds?: number[];
  userEmails?: string[];
  dropGlobalRoleCodes?: string[];
  wildcardAllowedRoleCodes?: string[];
  roleCodes?: string[];
  normalizeRolePermissions?: boolean;
}

export interface ExpiredUserRolesCleanupPayload {
  userIds?: number[];
  userEmails?: string[];
  roleCodes?: string[];
}

export interface CleanupSummary {
  [key: string]: number | string | boolean | null;
}

export interface CleanupResponse<TDetails = unknown> {
  applied: boolean;
  summary: CleanupSummary;
  details: TDetails;
}

/** What the tenant purge counts, before and after. */
export interface TenantPurgeSummary {
  organizations: number;
  companies: number;
  regions: number;
  locations: number;
  recipes: number;
  menus: number;
  materials: number;
  suppliers: number;
  cookingSessions: number;
  orders: number;
  scopedRoleAssignments: number;
  organizationRoles: number;
}

export interface TenantPurgeDetails {
  kept: string[];
  note: string;
}

export type TenantPurgeResponse = {
  applied: boolean;
  summary: TenantPurgeSummary;
  details: TenantPurgeDetails;
};

/**
 * The sentence the API demands verbatim before it will purge. Kept as one
 * constant the dialog also reads, so the phrase the user types is by
 * construction the phrase the API checks.
 */
export const TENANT_DATA_PURGE_PHRASE = 'PURGE ALL TENANT DATA';

/** What one run of the demo seed creates. */
export interface DemoOrgSeedSummary {
  organizations: number;
  companies: number;
  regions: number;
  locations: number;
  materials: number;
  storageContainers: number;
  tools: number;
  recipes: number;
  recipeIngredients: number;
  menus: number;
  menuItems: number;
}

export interface DemoOrgSeedRecipe {
  name: string;
  category: string;
  yield: string | null;
  shelfLifeDays: number;
  ingredients: number;
  steps: number;
  subRecipes: string[];
}

export interface DemoOrgSeedResponse {
  applied: boolean;
  organizationName: string;
  slug: string;
  /** Set on preview when the slug is taken; apply rejects instead. */
  conflict: { id: number; name: string; slug: string } | null;
  organizationId?: number;
  companyId?: number;
  summary: DemoOrgSeedSummary;
  details: { recipes: DemoOrgSeedRecipe[]; note: string };
}

/**
 * One default role, diffed against what the database currently holds.
 *
 * `before`/`after` are the whole permission arrays; `added`/`removed` are the
 * difference, which is what the card renders. A `missing` role was deleted and
 * would be recreated; a `drifted` role with empty `added`/`removed` had only
 * its name or active flag changed.
 */
export interface SystemRoleRestoreEntry {
  code: string;
  name: string;
  status: 'missing' | 'drifted' | 'in-sync';
  before: string[];
  after: string[];
  added: string[];
  removed: string[];
}

export interface SystemRolesRestoreSummary {
  rolesEvaluated: number;
  rolesDrifted: number;
  rolesMissing: number;
  rolesInSync: number;
  permissionsAdded: number;
  permissionsRemoved: number;
}

export interface SystemRolesRestoreResponse {
  applied: boolean;
  summary: SystemRolesRestoreSummary;
  details: { roles: SystemRoleRestoreEntry[] };
}

/**
 * The sentence the API demands verbatim before it will rewrite the default
 * roles. Same arrangement as the purge phrase: one constant, read by both the
 * dialog and the request, so what the user types is by construction what the
 * API checks.
 */
export const RESTORE_DEFAULT_ROLES_PHRASE = 'RESTORE DEFAULT ROLES';

export const maintenanceApi = {
  listTasks: () => api.get<CleanupTaskDescriptor[]>('/maintenance/cleanup/tasks'),

  previewSystemRoles: () =>
    api.post<SystemRolesRestoreResponse>('/maintenance/cleanup/system-roles/preview', {}),

  applySystemRoles: (payload: { confirm: true; confirmationPhrase: string }) =>
    api.post<SystemRolesRestoreResponse>('/maintenance/cleanup/system-roles/apply', payload),

  previewRolesPermissions: (payload: RolesPermissionsCleanupPayload) =>
    api.post<CleanupResponse>('/maintenance/cleanup/roles-permissions/preview', payload),

  applyRolesPermissions: (payload: RolesPermissionsCleanupPayload & { confirm: true }) =>
    api.post<CleanupResponse>('/maintenance/cleanup/roles-permissions/apply', payload),

  previewExpiredUserRoles: (payload: ExpiredUserRolesCleanupPayload) =>
    api.post<CleanupResponse>('/maintenance/cleanup/user-roles-expired/preview', payload),

  applyExpiredUserRoles: (payload: ExpiredUserRolesCleanupPayload & { confirm: true }) =>
    api.post<CleanupResponse>('/maintenance/cleanup/user-roles-expired/apply', payload),

  previewTenantPurge: () =>
    api.post<TenantPurgeResponse>('/maintenance/cleanup/tenant-data/preview', {}),

  applyTenantPurge: (payload: { confirm: true; confirmationPhrase: string }) =>
    api.post<TenantPurgeResponse>('/maintenance/cleanup/tenant-data/apply', payload),

  previewDemoOrgSeed: (payload: { organizationName: string }) =>
    api.post<DemoOrgSeedResponse>('/maintenance/cleanup/seed-demo-org/preview', payload),

  applyDemoOrgSeed: (payload: { organizationName: string; confirm: true }) =>
    api.post<DemoOrgSeedResponse>('/maintenance/cleanup/seed-demo-org/apply', payload),
};
